import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { metricKeys } from '@pulse/metrics';
import {
  getLegacySensitivity,
  getSensitivity,
  saveLegacySensitivityDataForMetric,
  saveSensitivityDataForMetric,
} from '@src/api/aiAlerts';
import { aiAlertsPCPFeatureSelector } from '@src/store/common/selectors';
import { trackQueryError } from '@src/utils/sensor/apptracker';
import { useGlobalFeature } from '@globalStore/globalFeatureFlagsStore';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';

export const useFetchSensitivity = (enabled) => {
  const isPcpFromNewSystem = useGlobalFeature(globalFeatureNames.IS_PCP_FROM_NEW_SYSTEM);
  const { data, isFetching, isError, refetch } = useQuery(
    ['ai-alert-sensitivity'],
    async () => await getSensitivity({}),
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled,
      onError: (err) => {
        trackQueryError({ type: 'ai-alert-sensitivity-error', err });
      },
    },
  );

  // The fusion GET returns { sensitivity, kpiId }; the legacy path returns a bare array.
  // Normalize both so downstream consumers keep receiving an array plus the (optional) kpiId.
  const payload = data?.data;
  const rows = Array.isArray(payload) ? payload : payload?.sensitivity ?? [];
  const kpiId = Array.isArray(payload) ? undefined : payload?.kpiId;

  // When isPCPFromNewSystem is off, ConcurrentPlays is handled by ConcurrentPlaysSection
  // via legacy-metrics (MySQL). Strip it here so the main form diff logic never marks
  // it isDeleted for fusion-OFF customers.
  const metricData = rows.filter(
    (row: any) => isPcpFromNewSystem || row?.metricKey !== metricKeys.ConcurrentPlays,
  );

  return { data: metricData, kpiId, isFetching, isError, refetch };
};

export const useUpdateSensitivity = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: any) => {
      return saveSensitivityDataForMetric(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ai-alert-sensitivity']);
        queryClient.invalidateQueries(['ai-alert-sensitivity-log']);
      },
    },
  );
};

export const useFetchLegacySensitivity = (enabled: boolean) => {
  const isPcpEnabled = useSelector(aiAlertsPCPFeatureSelector);
  const { data, isFetching, isError, refetch } = useQuery(
    ['ai-alert-sensitivity-legacy'],
    async () => await getLegacySensitivity({}),
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: enabled && !!isPcpEnabled,
      onError: (err) => {
        trackQueryError({ type: 'ai-alert-sensitivity-legacy-error', err });
      },
    },
  );

  const metricData = data?.data || [];

  return { data: metricData, isFetching, isError, refetch };
};

export const useUpdateLegacySensitivity = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: any) => {
      return saveLegacySensitivityDataForMetric(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ai-alert-sensitivity-legacy']);
        queryClient.invalidateQueries(['ai-alert-sensitivity-log']);
      },
    },
  );
};
